import { Card } from "./ui/card";
import { ComplianceChart } from "./ComplianceChart";
import { PropertyComplianceTable } from "./PropertyComplianceTable";
import "../styles/SAQDashboard.module.scss";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Icon } from "Components";
import { Divider, Input, Tooltip, Select, DatePicker, Pagination } from "antd";
import { GetGroupsListRequest, GetGroupsListResponse } from "Services/Api/Groups/Types";
import { useLazyQuery } from "@apollo/client";
import { GET_GROUPS_LIST } from "Services/Api/Groups/Queries";

import {
  getComplianceSummary,
  getVenzaProperties,
  getComplianceProgress,
  PropertyItem,
  PropertyFilterParams,
  ComplianceProgressItem,
  ProgressParams,
  getProvidersForTenant,
  VenzaPropertiesResponse,
  formatMonth
} from "Services/Api/PciCompliance/SAQDashboard.Service";

import moment, { Moment } from "moment";
import Alert from "Components/Alert/Alert";
import { useStores } from "Hooks/useStore";
import { SystemRoleType } from "Services/Api/Roles/Types";
import Spin from "Components/Spin/Spin";
import HeaderRow from "Pages/TenantProvider/components/HeaderRow";

const { Option } = Select;
const { RangePicker } = DatePicker;

/* -------------------------------
  TTM 12-MONTH FIXED WINDOW
--------------------------------*/
const getTTMRange = () => {
  const today = moment();
  return {
    from: today.format("YYYY-MM"),
    to: today.clone().add(11, "months").format("YYYY-MM"),
  };
};

/* ---- TYPES ---- */
type SortColumn = "name" | "status" | "dueDate" | "lastChange" | null;
type SortDirection = "asc" | "desc";

type ComplianceSummary = {
  no_of_saq: number;
  no_of_compliant_saq: number;
  no_of_non_compliant_saq: number;
  compliant_percentage: string;
  last_sync_date?: string | null;
};

type PropertyDataType = {
  propertyName: string;
  status: "Compliant" | "Non-Compliant" | "Not Started";
  dueDate: string | "—";
  lastChange: string | "—";
};

interface DashboardAlert {
  id: string;
  message: string;
}

const PER_PAGE_OPTIONS: (number | "All")[] = [10, 20, 50, 100, "All"];

// 1) SuperAdmin Query (Depth 1, using for dropdown population)
const BASE_TENANT_QUERY_VARIABLES = {
    sort: "name:asc",
    filters: {
        depth: 1,
        parentId: null
    }
} as const;

// 2) Admin Properties Query (isProperty: [true], using for populating RAW_GROUPS)
const ADMIN_PROPERTIES_QUERY_VARIABLES = {
    sort: "name:asc",
    filters: {
        isProperty: [true] 
    }
} as const;

// 3) Admin Query (Minimum filters, using only to get rootGroupId if needed)
const ADMIN_ROOT_CONTEXT_VARIABLES = {
    sort: "name:asc",
    filters: {} 
} as const;


export function Scanning() {
  /* ---- STORES / ROLE ---- */
  const { authStore } = useStores();

  const role = authStore.currentUser?.primarySystemRole?.role;
  const isSuperAdmin = role === SystemRoleType.SuperAdmin;
  const isAdmin = role === SystemRoleType.Admin;
  
  const adminGroupId = authStore.currentUser?.groups?.[0]?.id;

  const adminTenantId: string | undefined = adminGroupId
    ? String(adminGroupId)
    : undefined;

  const [adminRootTenantId, setAdminRootTenantId] = useState<string | undefined>(undefined);

  const primaryGroupQueryVariables: GetGroupsListRequest = useMemo(() => {
    
    // SuperAdmin: fetches top-level tenants for the dropdown
    if (isSuperAdmin) {
        return BASE_TENANT_QUERY_VARIABLES as unknown as GetGroupsListRequest;
    }
    
    // Admin: fetches properties only for the property list (RAW_GROUPS)
    if (isAdmin) {
        const adminVars = {
            ...ADMIN_PROPERTIES_QUERY_VARIABLES,
            filters: {
                isProperty: ADMIN_PROPERTIES_QUERY_VARIABLES.filters.isProperty as unknown as GetGroupsListRequest['filters']['isProperty'], 
            }
        };
        return adminVars as unknown as GetGroupsListRequest;
    }
    
    return BASE_TENANT_QUERY_VARIABLES as unknown as GetGroupsListRequest;
    
  }, [isSuperAdmin, isAdmin]);

  /* ---- STATES ---- */
  const [selectedClient, setSelectedClient] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [dueDateRange, setDueDateRange] = useState<[Moment | null, Moment | null]>([
    null,
    null,
  ]);
  const [lastChangeRange, setLastChangeRange] = useState<
    [Moment | null, Moment | null]
  >([null, null]);

  const [dueDateFilter, setDueDateFilter] = useState<any>({});
  const [lastChangeFilter, setLastChangeFilter] = useState<any>({});

  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number | "All">(10);
  const [totalProperties, setTotalProperties] = useState(0);

  const [complianceSummary, setComplianceSummary] =
    useState<ComplianceSummary | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  const [propertyList, setPropertyList] = useState<PropertyItem[]>([]);
  const [isPropertyLoading, setIsPropertyLoading] = useState(false);

  const [progressData, setProgressData] = useState<ComplianceProgressItem[]>([]);
  const [isProgressLoading, setIsProgressLoading] = useState(false);

  const [alertMessages, setAlertMessages] = useState<DashboardAlert[]>([]);

  const [providerId, setProviderId] = useState<number | null>(null);
  const [isTenantsLoaded, setIsTenantsLoaded] = useState(false);
  const [isProviderFetching, setIsProviderFetching] = useState(false);


  const clientSelectRef = useRef<any>(null);

  /* ---- GRAPHQL QUERIES ---- */
  //  Query (Properties for Admin or Tenants for SuperAdmin)
  const [getGroup, result] = useLazyQuery<GetGroupsListResponse, GetGroupsListRequest>(
    GET_GROUPS_LIST,
    {
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    }
  );

  const isTenantListLoading = result.loading;

  const RAW_GROUPS = useMemo(
    () => result?.data?.listGroups ?? [],
    [result.data]
  );

  // Query: To fetch Admin's true root group ID
  const [getAdminRootContext, adminRootQueryResult] = useLazyQuery<
    GetGroupsListResponse, 
    GetGroupsListRequest
  >(
    GET_GROUPS_LIST,
    {
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: false,
    }
  );
  
  
  const ALL_CLIENT_OPTIONS = useMemo(
    () => {
      const groupsWithEffectiveId = RAW_GROUPS.map((el) => {
        const effectiveId = el.rootGroupId ?? el.id;
        const effectiveGroup = RAW_GROUPS.find(g => String(g.id) === String(effectiveId));
        const effectiveName = effectiveGroup?.name ?? el.name;

        return {
          label: effectiveName,
          value: String(effectiveId),
          originalId: String(el.id),
          parentId: String(el.parentId),
          isProperty: !!el.isProperty,
        };
      });

      return groupsWithEffectiveId
        .filter((item, index, self) =>
          index === self.findIndex((t) => (
            t.value === item.value
          ))
        ) ?? [];
    },
    [RAW_GROUPS]
  );
  
  const adminEffectiveTenantId = useMemo(() => {
    if (!isAdmin || !adminTenantId) return undefined;

    if (isAdmin && adminRootTenantId) {
      return adminRootTenantId;
    }
    
    if (isAdmin && RAW_GROUPS.length > 0) {
        const rootIdFromProperty = RAW_GROUPS[0].rootGroupId ?? RAW_GROUPS[0].id;
        return String(rootIdFromProperty);
    }

    return adminTenantId;
    
  }, [isAdmin, adminTenantId, adminRootTenantId, RAW_GROUPS]);


  const propertyIdsForFilter = useMemo<number[] | undefined | null>(() => {
    if (isSuperAdmin) {
      return undefined;
    }
    

    const properties = RAW_GROUPS.filter(
      (el) =>
        el.isProperty &&
        String(el.rootGroupId ?? el.id) === selectedClient
    );

    const propIds = properties.map((p) => p.id);

    if (propIds.length > 0) {
      return propIds;
    } else {
      return null;
    }
  }, [isSuperAdmin, selectedClient, RAW_GROUPS]);


  useEffect(() => {
    if (result.data?.listGroups) setIsTenantsLoaded(true);
  }, [result.data]);


  useEffect(() => {

    if (isAdmin && !isTenantsLoaded && !adminRootTenantId && !adminRootQueryResult.loading) {

        getAdminRootContext({ variables: ADMIN_ROOT_CONTEXT_VARIABLES as unknown as GetGroupsListRequest });
    }
  }, [isAdmin, isTenantsLoaded, adminRootTenantId, adminRootQueryResult.loading, getAdminRootContext]);


  useEffect(() => {
    if (isAdmin && adminRootQueryResult.data?.listGroups.length && !adminRootTenantId) {

        const adminGroupData = adminRootQueryResult.data.listGroups.find(
             (g) => String(g.id) === adminTenantId
        );

        if (adminGroupData) {
            const effectiveId = adminGroupData.rootGroupId ?? adminGroupData.id;
            
            setAdminRootTenantId(String(effectiveId));
        } else {
            
             setAdminRootTenantId(adminTenantId);
        }
    }
  }, [isAdmin, adminRootQueryResult.data, adminRootTenantId, adminTenantId]);



  useEffect(() => {
    if (result.refetch) {
        result.refetch(primaryGroupQueryVariables);
    } else {
        getGroup({ variables: primaryGroupQueryVariables });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryGroupQueryVariables]);


  useEffect(() => {

    if (isAdmin && adminEffectiveTenantId && isTenantsLoaded && !selectedClient) {
      setSelectedClient(adminEffectiveTenantId);
    }

  }, [isAdmin, adminEffectiveTenantId, isTenantsLoaded, selectedClient]);


  const tenantOptions = useMemo(() => {
    return ALL_CLIENT_OPTIONS.map((tenant) => {
      const shouldDisable =
        isAdmin && adminEffectiveTenantId
          ? tenant.value !== adminEffectiveTenantId
          : false; // superAdmin / others enabled

      return {
        ...tenant,
        disabled: shouldDisable,
      };
    });
  }, [ALL_CLIENT_OPTIONS, isAdmin, adminEffectiveTenantId]);

  /* ---- ALERT HANDLER ---- */
  const handleApiError = (apiName: string, error: any) => {
    const msg = `${apiName} Data Not Found for selected client.`;
    if (!alertMessages.some((a) => a.id === apiName)) {
      setAlertMessages((prev) => [...prev, { id: apiName, message: msg }]);
    }
  };

  const removeAlert = (id: string) =>
    setAlertMessages((prev) => prev.filter((a) => a.id !== id));

  const selectedClientNameForApi = useMemo(() => {
    if (!selectedClient) return undefined;
    if (!isSuperAdmin) return undefined;

    return ALL_CLIENT_OPTIONS.find(t => t.value === selectedClient)?.label;
  }, [selectedClient, isSuperAdmin, ALL_CLIENT_OPTIONS]);

  /* ---- DATA FETCHING ---- */

  // 1. Fetch Provider ID based on selected client (Tenant)
  useEffect(() => {
    removeAlert("Compliance Summary");
    removeAlert("Property List");
    removeAlert("Compliance Progress");
    
    if (!selectedClient) {
      setProviderId(null);
      setIsProviderFetching(false);
      return;
    }
    
  
    setIsProviderFetching(true);

    async function fetchProvider() {
      try {
        const full = await getProvidersForTenant(selectedClient, selectedClientNameForApi);
        const providers = Array.isArray(full.data) ? full.data : [];

        const envName = (process.env.REACT_APP_SCANNING_DASHBOARD || "")
          .trim()
          .toLowerCase();

        const matched = providers.find(
          (x: { provider_id: number; provider_name: string }) =>
            x.provider_name.trim().toLowerCase() === envName
        );

        setProviderId(matched?.provider_id ?? null);
      } catch {
        setProviderId(null);
      } finally {
        setIsProviderFetching(false);
      }
    }

    fetchProvider();
  }, [selectedClient, isTenantsLoaded, selectedClientNameForApi]);

  const isInitialDataLoading = useMemo(() => {
    return isTenantListLoading || adminRootQueryResult.loading || (!!selectedClient && isProviderFetching);
  }, [isTenantListLoading, selectedClient, isProviderFetching, adminRootQueryResult.loading]);


  // 2. Fetch Compliance Summary
  useEffect(() => {

    if (!selectedClient || providerId == null) {
      setComplianceSummary(null);
      return;
    }

    async function fetchSummary() {
      setIsSummaryLoading(true);
      try {
        const tenantId = parseInt(selectedClient, 10);
        const summary = await getComplianceSummary(tenantId, providerId!);
        setComplianceSummary(summary);
      } catch (err) {
        setComplianceSummary(null);
        handleApiError("Compliance Summary", err);
      } finally {
        setIsSummaryLoading(false);
      }
    }

    fetchSummary();
  }, [selectedClient, providerId]);

  // 3. Fetch Property List (Main table data, filters and pagination)
  useEffect(() => {
    if (!selectedClient || providerId == null || propertyIdsForFilter === null) {
      setPropertyList([]);
      setTotalProperties(0);
      setIsPropertyLoading(false); 
      return;
    }

    //  Do not fetch if search query is provided but is less than 3 characters.
    if (searchQuery && searchQuery.length < 3) {
      return;
    }

    async function fetchProperties() {
      setIsPropertyLoading(true);
      try {
        const tenantId = parseInt(selectedClient, 10);

        const finalPerPage = perPage === "All"
          ? totalProperties || 10000
          : perPage as number;

        const params: PropertyFilterParams = {
          tenant_id: tenantId,
          provider_id: providerId!,
          property_name: searchQuery || undefined,
          status:
            statusFilter === "all"
              ? undefined
              : statusFilter === "compliant"
                ? "pass"
                : statusFilter === "non-compliant"
                  ? "fail"
                  : "notstarted",
          from_due_date: dueDateFilter.from_due_date || undefined,
          to_due_date: dueDateFilter.to_due_date || undefined,
          from_last_change_date: lastChangeFilter.from_last_change_date || undefined,
          to_last_change_date: lastChangeFilter.to_last_change_date || undefined,
          per_page: finalPerPage,
          page: perPage === "All" ? 1 : currentPage,
          select_property_ids: propertyIdsForFilter ?? undefined, 
        };

        const response: VenzaPropertiesResponse = await getVenzaProperties(params);
        const normalizedData = response.data.map((p: PropertyItem) => ({
          ...p,
          status: p.status && p.status.trim() !== ""
            ? p.status
            : "notstarted",
        }));

        const filteredData = normalizedData.filter(
          (p) =>
            p.status === "pass" ||
            p.status === "fail" ||
            p.status === "notstarted"
        );

        setPropertyList(filteredData);
        setTotalProperties(response.meta.total);

        
      } catch (err) {
        setPropertyList([]);
        setTotalProperties(0);
        handleApiError("Property List", err);
      } finally {
        setIsPropertyLoading(false);
      }
    }

    fetchProperties();
  }, [
    selectedClient,
    providerId,
    searchQuery,
    statusFilter,
    dueDateFilter.from_due_date,
    dueDateFilter.to_due_date,
    lastChangeFilter.from_last_change_date,
    lastChangeFilter.to_last_change_date,
    currentPage,
    perPage,
    totalProperties,
    propertyIdsForFilter,
  ]);

  // 4. Fetch Compliance Progress (Chart data)
  useEffect(() => {
    if (!selectedClient || providerId == null) {
      setProgressData([]);
      return;
    }

    async function fetchProgress() {
      setIsProgressLoading(true);
      try {
        const tenantId = parseInt(selectedClient, 10);
        const { from, to } = getTTMRange();

        const params: ProgressParams = {
          tenant_id: tenantId,
          provider_id: providerId!,
          period: "monthly",
        };

        const result = await getComplianceProgress(params);
        setProgressData(result);
      } catch (err) {
        setProgressData([]);
        handleApiError("Compliance Progress", err);
      } finally {
        setIsProgressLoading(false);
      }
    }

    fetchProgress();
  }, [selectedClient, providerId]);

  /* ---- PAGINATION & FILTER HANDLERS ---- */
  const handlePageChange = (page: number) => setCurrentPage(page);

  const handlePerPageChange = (value: number | "All") => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleSort = useCallback(
    (column: SortColumn) => {
      if (sortColumn === column)
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn, sortDirection]
  );

  /* ---- DATA PROCESSING FOR DISPLAY ---- */
  const totalSAQs = complianceSummary?.no_of_saq;
  const compliant = complianceSummary?.no_of_compliant_saq;
  const nonCompliant = complianceSummary?.no_of_non_compliant_saq;

  const formattedLastSyncDate = complianceSummary?.last_sync_date
    ? moment.utc(complianceSummary.last_sync_date).format("M/D/YYYY h:mm:ss A z")
    : "—";

  const compliancePercentage =
    complianceSummary?.compliant_percentage !== undefined
      ? parseFloat(complianceSummary.compliant_percentage)
      : totalSAQs && compliant
        ? Math.round((compliant / totalSAQs) * 100)
        : undefined;

  const filteredPropertyData: PropertyDataType[] = propertyList.map((p) => ({
    propertyName: p.property_name,
    status:
      p.status === "pass"
        ? "Compliant"
        : p.status === "fail"
          ? "Non-Compliant"
          : "Not Started",
    dueDate: p.due_date ? moment(p.due_date).format("M/D/YYYY") : "—",
    lastChange: p.last_change_date
      ? moment(p.last_change_date).format("M/D/YYYY")
      : "—",
  }));

  const isShowingAll = perPage === "All";
  const displayPerPage = isShowingAll ? totalProperties : (perPage as number);

  const startRange =
    isShowingAll && totalProperties === 0
      ? 0
      : Math.max(0, (currentPage - 1) * displayPerPage) + 1;
  const endRange = isShowingAll
    ? totalProperties
    : Math.min(currentPage * displayPerPage, totalProperties);

  /* ---- COMPONENT RENDER ---- */
  return (
    <div className="dashboard-compliance relative">
      {/* FULL PAGE LOADER OVERLAY */}
      {isInitialDataLoading && (
        <div className="absolute inset-0 bg-white/70 z-50 d-flex items-center justify-center min-h-[500px]">
          <Spin size="large" />
        </div>
      )}

      {/* Main content wrapper  */}
      <div className={isInitialDataLoading ? 'opacity-50 pointer-events-none' : ''}>
        <div className="space-y-2"></div>

        {/* API ALERTS */}
        {alertMessages.map((alert) => (
          <div className="mbx-32" key={alert.id}>
            <Alert
              message={alert.message}
              type="error"
              showIcon
              closable
              onClose={() => removeAlert(alert.id)}
            />
          </div>
        ))}

        {/* CLIENT SELECT/SEARCH */}
        <HeaderRow title={"Scanning"} subHeading={"Monitor scanning activity and results."} parentClass="mbx-32">
          {isSuperAdmin && (
            <div className="d-flex align-center gap-2">
              <label className="text-foreground-fade">Client</label>
              {isTenantListLoading ? (
                <div className="w-48 h-10 d-flex items-center justify-center">
                  <Spin size="small" />
                </div>
              ) : (
                <Select
                  ref={clientSelectRef}
                  placeholder="Select a client"
                  value={selectedClient || undefined} 
                  onChange={(val) => {
                    setSelectedClient(val as string);
                    setCurrentPage(1);
                    setAlertMessages([]);
                  }}
                  onSelect={() => {
                    clientSelectRef.current?.blur();
                  }}
                  className="custom-select"
                  suffixIcon={<Icon type="tenantSuffix" />}
                  options={tenantOptions}
                  dropdownMatchSelectWidth
                  getPopupContainer={(triggerNode) => triggerNode.parentElement!}
                  dropdownAlign={{
                    points: ["tl", "bl"],
                    overflow: { adjustY: false },
                  }}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              )}
            </div>
          )}
        </HeaderRow>

        <div className="space-y-8">
          <div>
            {/* OVERVIEW CARDS */}
            <div className="overviewWrapper grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              <Card className="cardWrapper">
                {/* Individual Card Loading (for Summary) */}
                {isSummaryLoading && (
                  <div className="absolute inset-0 bg-white/70 z-10 d-flex items-center justify-center rounded-lg">
                    <Spin size="large" />
                  </div>
                )}
                <div className={isSummaryLoading ? 'opacity-50' : ''}> 
                  <div className="mbx-24">
                    <h2 className="text-foreground fs-500">Scanning Overview</h2>
                  </div>

                  <Divider className="m-0 mbx-16 divider-border" />

                  <div className="mbx-24 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Number of SAQs Card */}
                    <Card className="cardChildWrapper p-5 sm:p-6 bg-gradient-to-br from-blue-50/50 to-white hover:shadow-md border-none border-blue-100/60">
                      <div className="d-flex flex-col justify-between h-full mh-120">
                        <div className="d-flex items-center gap-3">
                          <div className="svg-icon-wrapper svg-blue">
                            <Icon type="fileText" className="w-6 h-6 text-blue-600" />
                          </div>

                          <div className="d-flex items-center justify-between gap-1 w-full">
                          <p className="mb-0 fsx-18 text-muted-foreground">
                            Number of Scans
                          </p>
                          <Tooltip
                            title="Total number of scans assigned to your organisation, regardless of compliance status. All data is updated daily."
                            placement="top"
                            overlayClassName="custom-tooltip"
                          >
                            <Icon type="saqTooltip" className="saqTooltip" />
                          </Tooltip>
                        </div>

                        </div>
                        <div className="d-flex justify-end hx-40">
                          <p className="mb-0 fsx-36 text-blue-600 fw-500">
                            {isSummaryLoading ? "—" : totalSAQs ?? "—"}
                          </p>
                        </div>
                      </div>
                    </Card>

                    {/* SAQ Compliance Percentage Card */}
                    <Card className="cardChildWrapper p-5 sm:p-6 bg-gradient-to-br from-blue-50/50 to-white hover:shadow-md border-none border-blue-100/60">
                      <div className="d-flex flex-col justify-between h-full mh-120">
                        <div className="d-flex items-center gap-3">
                          <div className="svg-icon-wrapper svg-blue">
                            <Icon type="trendingArrow" className="w-6 h-6 text-blue-600" />
                          </div>

                          <div className="d-flex items-center justify-between gap-1 w-full">
                          <p className="mb-0 fsx-18 text-muted-foreground">
                            Scanning Compliance Percentage
                          </p>
                          <Tooltip
                            title="Scanning Compliance Percentage represents the proportion of scans that are fully compliant out of the total scans assigned."
                            placement="top"
                            overlayClassName="custom-tooltip"
                          >
                            <Icon type="saqTooltip" className="saqTooltip" />
                          </Tooltip>
                        </div>

                        </div>
                        <div className="d-flex justify-end hx-40">
                          <p className="fsx-36 text-blue-600 fw-500">
                            {isSummaryLoading
                              ? "—"
                              : compliancePercentage !== undefined
                                ? `${compliancePercentage}%`
                                : "—"}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>

                  <Divider className="m-0 mbx-16 divider-border" />

                  <div className="mbx-24">
                    <h2 className="text-foreground fs-500">Scanning Compliance Breakdown</h2>
                    <p className="text-muted-foreground-70 mt-0.5 fs-400">
                      Distribution of compliant vs non-compliant scans
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Compliant Card */}
                    <Card className="cardChildWrapper p-5 sm:p-6 bg-compliant-card hover:shadow-md border-none border-blue-100/60">
                      <div className="d-flex flex-col justify-between h-full mh-120">
                        <div className="d-flex items-center gap-3">
                          <div className="svg-icon-wrapper svg-green">
                            <Icon type="mappingComplete" className="complete-svg" />
                          </div>

                          <div className="d-flex items-center justify-between gap-1 w-full">
                            <p className="mb-0 fsx-18 text-muted-foreground">Compliant</p>
                            <Tooltip
                              title="Total number of Compliant scans assigned to your organisation. Compliant scans are fully completed with all elements meeting PCI DSS requirements."
                              placement="top"
                              overlayClassName="custom-tooltip"
                            >
                              <Icon type="saqTooltip" className="saqTooltip" />
                            </Tooltip>
                          </div>
                        </div>
                        <div className="d-flex justify-end hx-40">
                          <p className="fsx-36 text-green-600 fw-500">
                            {isSummaryLoading ? "—" : compliant ?? "—"}
                          </p>
                        </div>
                      </div>
                    </Card>

                    {/* Non-Compliant Card */}
                    <Card className="cardChildWrapper p-5 sm:p-6 bg-gradient-to-br from-blue-50/50 to-white hover:shadow-md border-none border-blue-100/60">
                      <div className="d-flex flex-col justify-between h-full mh-120">
                        <div className="d-flex items-center gap-3">
                          <div className="svg-icon-wrapper svg-red">
                            <Icon type="nonCompliantCard" />
                          </div>

                          <div className="d-flex items-center justify-between gap-1 w-full">
                            <p className="mb-0 fsx-18 text-muted-foreground">
                              Non-Compliant
                            </p>
                            <Tooltip
                              title="Total number of Non-Compliant scans assigned to your organisation. A Non-Compliant scan is one that has not been started or is incomplete.
"
                              placement="top"
                              overlayClassName="custom-tooltip"
                            >
                              <Icon type="saqTooltip" className="saqTooltip" />
                            </Tooltip>
                          </div>
                        </div>
                        <div className="d-flex justify-end hx-40">
                          <p className="fsx-36 text-red-600 fw-500">
                            {isSummaryLoading ? "—" : nonCompliant ?? "—"}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </Card>

              {/* COMPLIANCE CHART */}
              <Card className="compliance-chart">
                <div className="space-y-5 h-full d-flex flex-col">
                  <div className="mbx-24">
                    <div className="d-flex items-center gap-2">
                      <h3 className="text-foreground fs-500">
                        Monthly Scanning Compliance Progress
                      </h3>
                      <Tooltip
                        title="How Scanning Compliance Percentage has changed over time. This helps track improvements or regressions in PCI DSS compliance across an organisation."
                        placement="top"
                        overlayClassName="custom-tooltip"
                      >
                        <Icon type="saqTooltip" className="saqTooltip" />
                      </Tooltip>
                    </div>
                  </div>

                  <div className="flex-1 min-h-[300px]">
                    {selectedClient ? (
                      isProgressLoading ? (
                        <div className="h-full d-flex items-center justify-center">
                          <Spin />
                        </div>
                      ) : (
                        <ComplianceChart
                          data={progressData.map((item) => ({
                            month: formatMonth(item.month),
                            percentage: item.compliance_percentage,
                          }))}
                        />
                      )
                    ) : (
                      <div className="h-full d-flex items-center justify-center text-muted-foreground/40">
                        Select a client to view progress
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>

            {/* PROPERTY LIST FILTERS AND TABLE */}
            {selectedClient && (
              <div className="space-y-4 pt-2">

                <div className="d-flex w-full items-stretch lg:items-center gap-3 flex-wrap filter-row">
                  {/* SEARCH INPUT */}
                  <div className="mtx-8 mbx-16 flex-1 min-w-[200px]">
                    <div className="relative flex-1">
                      <Icon type="saqSearch" />
                      <Input
                        placeholder="Search properties..."
                        value={searchQuery}
                        className="dashboard-search w-full"
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </div>

                  {/* STATUS FILTER */}
                  <div className="status-ant-select w-12">
                    <Select
                      value={statusFilter}
                      onChange={(val) => {
                        setStatusFilter(val);
                        setCurrentPage(1);
                      }}
                      suffixIcon={<Icon type="antSelectSuffix" />}
                      defaultValue={"All"}
                      className="custom-ant-select w-full"
                      dropdownMatchSelectWidth
                      getPopupContainer={(triggerNode) => triggerNode.parentElement!}
                      dropdownClassName="custom-select-dropdown"
                      dropdownAlign={{
                        points: ["tl", "bl"],
                        overflow: { adjustY: false },
                      }}
                    >
                      <Option value="all" label="All">
                        All
                      </Option>

                      <Option value="compliant">
                        <div className="option-item">
                          <Icon
                            type="completed"
                            className="compliant-icon-style"
                          />
                          Compliant
                        </div>
                      </Option>

                      <Option value="non-compliant">
                        <div className="option-item">
                          <Icon
                            type="nonCompliant"
                            className="non-compliant-icon-style"
                          />
                          Non-Compliant
                        </div>
                      </Option>


                      <Option value="not-started">
                        <div className="option-item">
                          <Icon
                            type="nonCompliant"
                            className="non-compliant-icon-style"
                          />
                          Not Started
                        </div>
                      </Option>
                    </Select>
                  </div>

                  {/* DUE DATE RANGE PICKER */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg dashboard-due-last-date-main-div flex-1 min-w-[250px]">
                    <span className="dashboard-due-last-date-label">Due Date:</span>
                    <RangePicker
                      format="YYYY-MM-DD"
                      allowClear
                      placeholder={["From", "To"]}
                      value={dueDateRange}
                      onChange={(dates) => {
                        setDueDateRange(dates ?? [null, null]);
                        setDueDateFilter({
                          from_due_date: dates?.[0]?.format("YYYY-MM-DD"),
                          to_due_date: dates?.[1]?.format("YYYY-MM-DD"),
                        });
                        setCurrentPage(1);
                      }}
                      className="dashboard-due-last-date-picker w-full"
                      getPopupContainer={() => document.body}
                    />
                  </div>

                  {/* LAST CHANGE RANGE PICKER */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg dashboard-due-last-date-main-div flex-1 min-w-[250px]">
                    <span className="dashboard-due-last-date-label">
                      Last Change:
                    </span>
                    <RangePicker
                      format="YYYY-MM-DD"
                      allowClear
                      placeholder={["From", "To"]}
                      value={lastChangeRange}
                      onChange={(dates) => {
                        setLastChangeRange(dates ?? [null, null]);
                        setLastChangeFilter({
                          from_last_change_date:
                            dates?.[0]?.format("YYYY-MM-DD"),
                          to_last_change_date: dates?.[1]?.format("YYYY-MM-DD"),
                        });
                        setCurrentPage(1);
                      }}
                      className="dashboard-due-last-date-picker w-full"
                      getPopupContainer={() => document.body}
                    />
                  </div>
                </div>

                {/* Property Table */}
                <div className="property-table-container relative min-h-[100px]">
                  {/* Table Loading Spin */}
                  {isPropertyLoading ? (
                    <div className="absolute inset-0 bg-white/70 z-10 d-flex items-center justify-center">
                      <Spin size="large" />
                    </div>
                  ) : (
                    <PropertyComplianceTable
                      properties={filteredPropertyData}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  )}
                </div>

                {/* FOOTER & PAGINATION */}
                <div className="d-flex justify-between items-center py-2 pagination-section">
                  {/* LEFT SIDE — Summary + Rows per page */}
                  <div className="d-flex items-center gap-4">
                    <div className="text-muted-foreground fs-1">
                      Showing {startRange} - {endRange} of {totalProperties}{" "}
                      properties
                    </div>

                    {/* Rows Per Page Selector */}
                    <div className="d-flex items-center gap-2">
                      <span className="text-muted-foreground fs-1">
                        Rows per page:
                      </span>

                      <Select
                        value={perPage}
                        onChange={handlePerPageChange}
                        className="w-16"
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={() => document.body}
                      >
                        {PER_PAGE_OPTIONS.map((option) => (
                          <Option key={option} value={option}>
                            {option}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  {/* RIGHT SIDE — Pagination OR Last Sync */}
                  {perPage !== "All" && totalProperties > Number(perPage) ? (
                    <Pagination
                      current={currentPage}
                      pageSize={Number(perPage)}
                      total={totalProperties}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                      disabled={isPropertyLoading}
                    />
                  ) : (
                    <p className="text-muted-foreground-70 fs-1 m-0">
                      <span>Last Sync:</span>{" "}
                      <span>{formattedLastSyncDate}</span>
                    </p>
                  )}
                </div>

                {/* WHEN PAGINATION EXISTS → Last Sync below */}
                {perPage !== "All" &&
                  totalProperties > Number(perPage) && (
                    <div className="d-flex justify-end pt-4 pb-2 last-sync-wrapper">
                      <p className="text-muted-foreground-70 fs-1 m-0">
                        <span>Last Sync:</span>{" "}
                        <span>{formattedLastSyncDate}</span>
                      </p>
                    </div>
                  )}
              </div>
            )}

            {/* EXTERNAL PORTAL LINK */}
            <div className="pt-2 border-t border-border/60">
              <div className="flex flex-col items-center text-center p-[1em] p-4 rounded-lg">
                <p className="text-foreground/75 text-[12px] mb-0">
                  To access detailed PCI DSS compliance and scanning completion information, log in to your{" "}
                  <a
                    href="https://pcicompliance.ws/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 decoration-[#0093D0]/40 text-[#0093D0] hover:text-[#0078ab]"
                  >
                    PCI Compliance Portal
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}